import type { SnackKey } from 'config/notifications'

import { appSelectors } from '@appStore/reducers/app'
import CircularProgress from '@mui/material/CircularProgress'
import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import HintTooltip from 'components/atoms/HintTooltip'
import ModalWindow from 'components/molecules/ModalWindow'
import { CONSTS } from 'config/objectConst'
import { notificationHooks } from 'features/notifications'
import { useMemo, useState } from 'react'
import { appConnector } from 'shared/connector'
import { useAppSelector, useSwitcher } from 'shared/hooks'
import { useConfirmState } from 'shared/odata/confirm'
import { Button, StackIcon } from 'shared/ui'
import { isValidOnBoardingConditions } from 'tools/function.types'

import { api } from '../../api'
import { useCurrent, useRun } from '../../hooks'

type ConfirmType = '1.2' | '1.5'

export function ActionControlButtons({ className }: { className?: string }) {
	const { campaign, id } = useCurrent()

	const [runCampaign, runQuery] = useRun()

	const actions = campaign?.actions

	const infoValue = campaign?.state

	const appProfile = useAppSelector(appSelectors.appProfile)

	const modalSwitcher = useSwitcher(false)

	const [getPreview, previewQuery] = api.useCampaignPreviewMutation()
	const [stop, stopQuery] = api.useStopCampaignMutation()
	const [complete, completeQuery] = api.useCompleteCampaignMutation()
	const confirm = useConfirmState()
	const [type, setType] = useState<ConfirmType>('1.2')

	async function runAction(mutate: typeof complete, key: SnackKey) {
		const { data } = await mutate(id)

		if (data) {
			snack(key)

			appConnector.auth.refreshAccessToken()
		}
	}

	const completeCampaign = () => runAction(complete, '3.8')

	const startCampaign = () => {
		runCampaign()
	}

	const actionMap: Record<ConfirmType, () => any | Promise<any>> = {
		'1.2': completeCampaign,
		'1.5': startCampaign
	}

	async function actionHandler() {
		modalSwitcher.off()
		const fetchData = actionMap[type]
		await fetchData()
	}

	const stopCampaign = () => runAction(stop, '3.7')

	const snack = notificationHooks.useSnack()

	async function startHandler() {
		if (infoValue === 'Stopped') {
			return startCampaign()
		}

		const { data } = await getPreview(id)
		if (!data) {
			return
		}
		if (!data.validation_rules_summary.length) {
			setType('1.5')
			modalSwitcher.on()
		} else {
			confirm.set({
				data,
				id,
				status: true
			})
		}
	}

	const modalContent = useMemo(() => {
		const data = CONSTS.confirmationDialogs[type]

		return {
			...data,
			content: data.content.replace(
				'{contacts_count}',
				String(previewQuery.data?.valid_audience_count ?? 0)
			)
		}
	}, [type, previewQuery.data?.valid_audience_count])

	const isLoading =
		runQuery.isLoading ||
		stopQuery.isLoading ||
		previewQuery.isLoading ||
		completeQuery.isLoading

	return [
		isLoading ? (
			<CircularProgress
				classes={{ colorPrimary: 'material-loader' }}
				color='primary'
				key='⏳'
				size={16}
				style={{ alignSelf: 'center' }}
			/>
		) : (
			[
				actions?.complete.enabled && (
					<Button
						className={className}
						key='🏁'
						onClick={() => {
							setType('1.2')
							modalSwitcher.on()
						}}
						variant='bordered-dark'
					>
						<svg
							aria-hidden
							fill='currentcolor'
							height={18}
							viewBox='0 0 18 18'
							width={18}
						>
							<rect height={12} rx={1} width={12} x={2} y={2} />
						</svg>

						{actions.complete.title}
					</Button>
				),

				actions?.stop.enabled && (
					<Button className={className} key='🛑' onClick={stopCampaign}>
						<StackIcon icon='stop' />

						{actions.stop.title}
					</Button>
				),

				actions?.run.enabled && (
					<div key='🏃' style={{ position: 'relative' }}>
						<Button
							className={className}
							disabled={
								campaign?.target_audience_count === 0 ||
								campaign?.steps.length === 0
							}
							onClick={startHandler}
						>
							<StackIcon icon='start' />
							{actions.run.title}
						</Button>

						<HintTooltip
							classes='start-camping-tooltip'
							enabled={
								isValidOnBoardingConditions(appProfile, '2.7') &&
								!localStorage.getItem('2.7')
							}
							handlerClose={() => {
								localStorage.setItem('2.7', 'true')
							}}
							key='hint'
							placement='bottom-end'
							tooltip={CONSTS.ON_BOARDING['2.7']}
						/>
					</div>
				)
			]
		),

		<ModalWindow
			closeHandler={modalSwitcher.off}
			closeText='No'
			confirmHandler={actionHandler}
			confirmText='Yes'
			{...modalContent}
			key='⚠️'
			open={modalSwitcher.isOn}
		/>,

		<ModalWindow
			classes={'confirmation-launch-modal'}
			closeHandler={confirm.reset}
			key='✅'
			open={confirm.state.status}
		>
			<ConfirmationDialog
				closeHandler={confirm.reset}
				confirmationDialog={confirm.state}
				startCampaign={startCampaign}
			/>
		</ModalWindow>
	]
}
